import { ChangeCircle as ChangeCircleIcon } from '@mui/icons-material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import {
  Avatar,
  Badge,
  Card,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import Hidden from '@mui/material/Hidden';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { styled } from '@mui/system';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDailogDisintegrate from 'src/components/ConfirmationDailogDisintegrate';
import Label from 'src/components/Label';
import { StyledComponent } from 'src/components/styled-components/styledComponent';
import HttpServices from 'src/services/httpService';
import { integrateModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import DynamicHeightComponent from 'src/utils/dynamicSize.component';
import { useReAuth } from 'src/utils/reAuth/ReAuthContext';
import { useToaster } from 'src/utils/toaster/toasterContext';
import Facebook from '../../assets/facebook.svg';
import BlueSky from '../../assets/bluesky.svg';
import GBusiness from '../../assets/google-my-business-logo.svg';
import Instagram from '../../assets/instagram.svg';
import LinkedIn from '../../assets/linked_in.svg';
import Pinterest_Icon from '../../assets/pinterest-icon.svg';
import Tiktok from '../../assets/tiktok.svg';
import Twitter from '../../assets/twitter.svg';
import SnapChat from '../../assets/snapchat.svg';
import YouTube from '../../assets/youtube_social_icon_red 128px.png';
import Threads_Icon from '../../assets/threads.svg';
import Scrollbar from '../../components/scrollbar';
import { UserListHead, UserListToolbar } from '../../sections/@dashboard/user';
import CommonReAuthenticateComponent from './commonReAuthenticate';
import FacebookReAuthenticateAll from './re-authenticate-accounts/facebookReAuthenticateAll';
import IntegratedActionPopup from './integratedActionPopup';
import { PostUtil } from '../new-post-new/post-util';

const IntegratdAccounts = ({ isShowOfferBanner = false }) => {
  const { t: translate } = useTranslation();
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('name');
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterName, setFilterName] = useState('');
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [integratedAccountData, setIntegratedAccountData] = useState([]);
  const [filteredAccounts, setFilteredAccounts] = useState([]);
  const [toBeDeletedId, setToBeDeletedId] = useState(null);
  const [toBeUpdate, setToBeUpdate] = useState(null);
  const [popoverElement, setPopoverElement] = useState(null);
  const [selectedPopOverAccount, setPopOverAccount] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [isNotFound, setIsNotFound] = useState(true);
  const { showToast } = useToaster();
  const [reAuthenticateOpen, setReAuthenticateOpen] = useState(false);
  const { checkAndTriggerReAuth } = useReAuth();
  const [reAuthAllOpen, setReAuthAllOpen] = useState(false);
  const isOrgAdminHasPermission = CommonHelper.CheckForPermissionAvailableForUser(
    CommonHelper.PermissionList.ORG_ADMIN
  );

  // Add these two handlers alongside the existing handleCloseReauthenticate
  const handleReAuthAll = () => {
    setReAuthAllOpen(true);
  };
  const handleCloseReAuthAll = () => {
    setReAuthAllOpen(false);
  };
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const responseHeight = isMobile
    ? 'calc(100vh - 335px)'
    : isShowOfferBanner
    ? 'calc(100vh - 365px)'
    : 'calc(100vh - 315px)';
  const getTableDesk = [
    { id: 'profile_name', label: `${translate('common.name')}`, alignLeft: true },
    { id: 'service_name', label: `${translate('common.social_media')}`, alignRight: false },
    { id: 'profile_type', label: `${translate('common.type')}`, alignRight: false },
    { id: 'token_status', label: `${translate('common.token_status')}`, alignRight: false },
    { id: 'account_status', label: `${translate('common.account_status')}`, alignRight: false },
    { id: 'action', label: `${translate('common.action')}`, alignCenter: true },
  ];
  const getTableMobile = [
    { id: 'profile_name', label: `${translate('common.name')}`, alignRight: false },
    { id: 'account_status', label: `${translate('common.status')}`, alignRight: false },
    { id: 'action', label: `${translate('common.action')}`, alignRight: false },
  ];
  const TABLE_HEAD = isMobile ? getTableMobile : getTableDesk;

  useEffect(() => {
    getConnectedNetworks();
  }, []);

  const [isLoadingOn, setLoadingOn] = useState(false);
  const connectedNetworksSuccessCallback = (data, message) => {
    setIntegratedAccountData(data);
    setFilteredAccounts(data);
    setIsNotFound(data && data.length === 0);
    checkAndTriggerReAuth(data);
  };

  const connectedNetworksFailureCallback = (message) => {
    showToast(message, 'e');
  };

  const getConnectedNetworks = () => {
    let getProps = {
      url: integrateModuleURL.getConnectedNetworks,
      successCallback: connectedNetworksSuccessCallback,
      failureCallback: connectedNetworksFailureCallback,
    };
    HttpServices.Get(getProps);
  };

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
    filteredAccounts.sort((a, b) => {
      return a;
    });
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) setSelectedAccounts([...filteredAccounts]);
    else setSelectedAccounts([]);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
  };

  const handleFilterByName = (event) => {
    const filterName = event.target.value.toLowerCase();
    setFilterName(filterName);
    const filteredData = integratedAccountData.filter((account) =>
      account.profile_name.toLowerCase().includes(filterName)
    );
    setFilteredAccounts(filteredData);
    setIsNotFound(filteredData.length === 0);
    setPage(0);
  };

  const disIntegrateSuccessCallback = (data, message) => {
    getConnectedNetworks();
    showToast(message, 's');
    setLoadingOn(false);
    setConfirmOpen(false);
    handleCloseMenu();
  };

  const disIntegrateFailureCallback = (message) => {
    showToast(message, 'e');
    setLoadingOn(false);
    setConfirmOpen(false);
    handleCloseMenu();
  };

  const handleDisAntegrateAccount = () => {
    setLoadingOn(true);
    let bodyProps = {
      accounts: [
        {
          id: toBeDeletedId?.id,
          service_id: toBeDeletedId?.service_id,
        },
      ],
    };
    let postProps = {
      url: integrateModuleURL.disintegrateAccounts,
      body: bodyProps,
      successCallback: disIntegrateSuccessCallback,
      failureCallback: disIntegrateFailureCallback,
    };
    HttpServices.Post(postProps);
  };

  const handleDelete = (selectedAccount) => {
    setToBeDeletedId(selectedAccount);
    setConfirmOpen(true);
  };
  const handleUpdateAccount = (selectedAccount) => {
    let fullurl = '';
    let bodyProps = {
      account_id: selectedAccount?.id,
    };
    switch (selectedAccount.service_name) {
      case 'twitter':
        fullurl = integrateModuleURL.updateTwitterAccounts;
        break;
      case 'facebook':
        fullurl = integrateModuleURL.updateFacebookAccounts;
        break;
      case 'youtube':
        fullurl = integrateModuleURL.updateYoutubeAccounts;
        break;
      case 'instagram':
        if (selectedAccount?.profile_type === PostUtil.ProfileType.DIRECT_PROFILE) {
          fullurl = integrateModuleURL.directUpdateInstagramAccounts;
        } else {
          fullurl = integrateModuleURL.updateInstagramAccounts;
        }
        break;
      case 'linkedin':
        fullurl = integrateModuleURL.updateLinkedinAccounts;
        bodyProps.profile_type = selectedAccount.profile_type;
        break;
      case 'gbusiness':
        fullurl = integrateModuleURL.updateGbusinessAccounts;
        break;
      case 'pinterest':
        fullurl = integrateModuleURL.updatePinterestAccounts;
        break;
      case 'tiktok':
        fullurl = integrateModuleURL.updateTiktokAccounts;
        break;
      case 'threads':
        fullurl = integrateModuleURL.updateThreadsAccounts;
        break;
      case 'bluesky':
        fullurl = integrateModuleURL.updateBlueskyAccounts;
        break;
      case 'snapchat':
        fullurl = integrateModuleURL.updatSnapChatAccounts;
        break;
      default:
        return null;
    }
    let postProps = {
      url: fullurl,
      body: bodyProps,
      successCallback: disIntegrateSuccessCallback,
      failureCallback: disIntegrateFailureCallback,
    };
    HttpServices.Post(postProps);
  };
  const handleReAuthenticate = (selectedAccount) => {
    setPopOverAccount(selectedAccount);
    setReAuthenticateOpen(true);
  };
  const handleViewLink = (selectedAccount) => {
    if (selectedAccount?.profile_link) {
      window.open(selectedAccount.profile_link, '_blank');
    }
    handleCloseMenu();
  };

  const getAvatar = (service_name) => {
    switch (service_name) {
      case 'twitter':
        return <SmallAvatar_t alt="twitter" src={Twitter} />;
      case 'facebook':
        return <SmallAvatar_f alt="facebook" src={Facebook} />;
      case 'bluesky':
        return <SmallAvatar_f alt="bluesky" src={BlueSky} />;
      case 'youtube':
        return <img alt="youtube" src={YouTube} />;
      case 'pinterest':
        return <img alt="youtube" src={Pinterest_Icon} />;
      case 'instagram':
        return <SmallAvatar_i alt="instagram" src={Instagram} />;
      case 'gbusiness':
        return <SmallAvatar_g alt="gbusiness" src={GBusiness} variant="rounded" />;
      case 'linkedin':
        return <SmallAvatar_l alt="linkedin" src={LinkedIn} variant="rounded" />;
      case 'tiktok':
        return <SmallAvatar_l alt="linkedin" src={Tiktok} />;
      case 'threads':
        return <SmallAvatar_l alt="Threads" src={Threads_Icon} />;
      case 'snapchat':
        return <SmallAvatar_l alt="Threads" src={SnapChat} />;
      default:
        return null;
    }
  };

  const handleAccountStatus = (row) => {
    let bodyProps = {
      accounts: [
        {
          id: row?.id,
          service_id: row?.service_id,
        },
      ],
    };
    let postProps = {
      url: row.is_enabled ? integrateModuleURL.disableSocialAccounts : integrateModuleURL.enableSocialAccounts,
      body: bodyProps,
      successCallback: disIntegrateSuccessCallback,
      failureCallback: disIntegrateFailureCallback,
    };
    HttpServices.Post(postProps);
  };

  const handleOpenMenu = (event, row) => {
    setPopOverAccount(row);
    setPopoverElement(event.currentTarget);
  };
  const handleCloseReauthenticate = () => {
    setReAuthenticateOpen(false);
    setPopoverElement(null);
  };

  const handleCloseMenu = () => {
    setPopOverAccount(null);
    setPopoverElement(null);
  };

  //Styled Components
  const SmallAvatar_f = styled(Avatar)(({}) => ({
    width: 20,
    height: 20,
    overflow: 'hidden',
    backgroundColor: '#ffffff',
  }));

  const SmallAvatar_i = styled(Avatar)(({}) => ({
    width: 20,
    height: 20,
    overflow: 'hidden',
  }));
  const SmallAvatar_g = styled(Avatar)(({}) => ({
    width: 23,
    height: 23,
    overflow: 'hidden',
  }));

  const SmallAvatar_t = styled(Avatar)(({}) => ({
    width: 20,
    height: 20,
    backgroundColor: '#ffffff',
  }));

  const SmallAvatar_l = styled(Avatar)(({}) => ({
    width: 23,
    height: 23,
    overflow: 'hidden',
  }));

  const postActionProps = {
    popoverElement: popoverElement,
    handleCloseMenu,
    handleUpdateAccount: () => handleUpdateAccount(selectedPopOverAccount),
    handleDeletePost: () => handleDelete(selectedPopOverAccount),
    handleReAuthenticate: () => handleReAuthenticate(selectedPopOverAccount),
    handleViewLink: () => handleViewLink(selectedPopOverAccount),
    selectedPopOverPost: selectedPopOverAccount,
  };

  return (
    <>
      <Card sx={{ mt: isMobile ? 2 : 3 }}>
        <UserListToolbar
          numSelected={selectedAccounts.length}
          filterName={filterName}
          onFilterName={handleFilterByName}
          handleDelete={handleDelete}
          handleReAuthAll={handleReAuthAll}
          reAuth={true}
        />
        <DynamicHeightComponent>
          <Scrollbar>
            <TableContainer sx={{ minHeight: responseHeight, maxHeight: responseHeight }}>
              <Table aria-label="collapsible table" stickyHeader>
                <UserListHead
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={integratedAccountData.length}
                  numSelected={selectedAccounts.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                  isShowCheckbox={false}
                />
                {isNotFound ? (
                  <TableBody>
                    <TableRow>
                      <TableCell align="center" colSpan={6} sx={{ py: 3 }}>
                        <Paper sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" paragraph>
                            {translate('common.not_found')}
                          </Typography>
                        </Paper>
                      </TableCell>
                    </TableRow>
                  </TableBody>
                ) : (
                  filteredAccounts.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((row, i) => {
                    const {
                      id,
                      service_name,
                      profile_name,
                      profile_image,
                      profile_type = '',
                      is_active,
                      is_enabled,
                    } = row;
                    const isSelected = selectedAccounts.findIndex((a) => a.id === row.id) >= 0;
                    return (
                      <TableBody key={i}>
                        <TableRow hover key={i} tabIndex={-1} role="checkbox" selected={isSelected}>
                          <TableCell align="center">
                            <Stack direction="row" alignItems="center" spacing={2}>
                              <Badge
                                overlap="circular"
                                style={{ color: '#ffffff' }}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                badgeContent={getAvatar(service_name)}
                              >
                                <Avatar
                                  src={profile_image ? profile_image : profile_name || id}
                                  alt={profile_name ? profile_name : id}
                                  sx={{
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    textAlign: 'center',
                                    height: 40,
                                    width: 40,
                                    bgcolor: '#00AB55',
                                  }}
                                  style={{ color: '#ffffff' }}
                                />
                              </Badge>
                              <Typography variant="subtitle2" noWrap>
                                {profile_name ? profile_name : id}
                              </Typography>
                            </Stack>
                          </TableCell>
                          <Hidden only={['xs']}>
                            <TableCell align="left">{CommonHelper.DisplayServiceName(service_name)}</TableCell>
                            <TableCell align="left">{profile_type}</TableCell>
                            <TableCell align="left">
                              {!is_active ? (
                                <Tooltip title={translate('integration.click_to_reauthenticate')}>
                                  <span>
                                    <Label
                                      color="error"
                                      onClick={() => handleReAuthenticate(row)}
                                      sx={{ cursor: 'pointer' }}
                                    >
                                      {translate('common.in_active')}
                                    </Label>
                                  </span>
                                </Tooltip>
                              ) : (
                                <Label color="success">{translate('common.active')}</Label>
                              )}
                            </TableCell>
                          </Hidden>
                          <TableCell align="left">
                            <StyledComponent.SwitchIcon
                              sx={{ m: 1 }}
                              checked={is_enabled}
                              onChange={() => {
                                handleAccountStatus(row);
                              }}
                            ></StyledComponent.SwitchIcon>
                          </TableCell>
                          <TableCell align="center" sx={{ width: '150px' }}>
                            {' '}
                            {!is_active && (
                              <Tooltip title={translate('integration.reauthenticate')}>
                                <IconButton size="small" onClick={() => handleReAuthenticate(row)}>
                                  <ChangeCircleIcon sx={{ color: 'error.main' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton size="small" onClick={(e) => handleOpenMenu(e, row)}>
                              <MoreVertIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    );
                  })
                )}
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              rowsPerPageOptions={[10, 15, 25]}
              count={integratedAccountData.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Scrollbar>
        </DynamicHeightComponent>
        <IntegratedActionPopup {...postActionProps} />
        <ConfirmationDailogDisintegrate
          open={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          loading={isLoadingOn}
          onConfirm={handleDisAntegrateAccount}
          title={translate('common.confirm_deletion')}
          message={`${translate('common.confirm_delete_account')}?`}
        />

        {reAuthenticateOpen && (
          <CommonReAuthenticateComponent
            selectedPopOverAccount={selectedPopOverAccount}
            getConnectedNetworks={getConnectedNetworks}
            onClose={handleCloseReauthenticate}
          />
        )}
        {reAuthAllOpen && (
          <FacebookReAuthenticateAll getConnectedNetworks={getConnectedNetworks} handleClose={handleCloseReAuthAll} />
        )}
      </Card>
    </>
  );
};

export default IntegratdAccounts;
